import { useCallback, useState } from "react";
import { FinalAmountWarning } from "@/types";
import { getFinalAmountError } from "@/utils/apiErrors";

/**
 * The state of the backend's final amount warning (APP 188): the warning itself and the request it is about. The
 * request is kept unchanged so that confirming resends exactly what the warning was given for, never a later edit.
 */
export const useFinalAmountWarning = <TRequest>() => {
  const [state, setState] = useState<{ warning: FinalAmountWarning; request: TRequest; opened: boolean } | null>(null);

  /** Opens the warning when `error` is a 188 for `request`, replacing any earlier one. Returns whether it was. */
  const catchWarning = useCallback((error: unknown, request: TRequest) => {
    const answer = getFinalAmountError(error);
    if (answer?.kind !== "warning") return false;
    setState({ warning: answer.warning, request, opened: true });
    return true;
  }, []);

  const close = useCallback(() => setState((previous) => previous && { ...previous, opened: false }), []);

  return {
    /** The last warning. It stays once closed, as the dialog still shows it while it fades out. */
    warning: state?.warning ?? null,
    /** The request the open warning is about. Null once it is closed, so a dialog fading out has nothing to send. */
    request: state?.opened ? state.request : null,
    opened: !!state?.opened,
    catchWarning,
    close,
  };
};
